import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import AppBar from '@pray/shared/components/ui/AppBar';
import Button from '@pray/shared/components/ui/Button';
import Text from '@pray/shared/components/ui/Text';
import { plans } from '@pray/shared/data/pricing/plans';
import type { RecurringPeriod } from '@pray/shared/data/pricing/types';
import type { IntervalPlan } from '@pray/shared/services/studioService/credits/types';
import { cn } from '@pray/shared/utils/styles';

import { VIEW_EVENT_NAMES } from 'constants.js';

import CheckoutCreditPlan from '../components/CheckoutCreditPlan';
import { HUBSPOT_STEP_VIEW_EVENT_NAMES } from '../utils/constants';
import { hubspotTrackStepView } from '../utils/step-tracking';

import styles from './PricingPaywallOnboardingStep.scss';

export default function CheckoutOnboardingStep({ form, onContinue, onBack, ...props }) {
  const { hasLogo, hasAppBar, showBackButton, showSkipButton, logo, subtitle } = props;

  const [searchParams, setSearchParams] = useSearchParams();
  const productId = searchParams.get('product_id');
  const interval = searchParams.get('interval') as RecurringPeriod;

  const plan = plans.find((plan) => plan.pricing[interval].id === productId);
  const product = plan?.pricing[interval];

  useEffect(() => {
    hubspotTrackStepView(HUBSPOT_STEP_VIEW_EVENT_NAMES.CHECKOUT);
  }, []);

  const skipButton = (
    <Button onClick={() => onContinue({ isSkipped: true })} style={{ paddingRight: 0 }}>
      Skip
    </Button>
  );

  const handleSuccess = () => {
    onContinue();
  };

  const handleBack = () => {
    setSearchParams((prev) => {
      prev.delete('product_id');
      prev.delete('interval');
      return prev;
    });
    onBack();
  };

  const intervalPlan: IntervalPlan = {
    id: product?.id,
    name: plan?.name,
    sku: product?.sku,
    price: product?.price,
    credits_granted: product?.credits,
    subscription_interval: interval,
    is_free_trial_enabled: product?.isFreeTrialEnabled,
  };

  return (
    <div data-viewname={VIEW_EVENT_NAMES.ONBOARDING_FLOW.CREDITS_PLAN_CHECKOUT}>
      {hasAppBar && (
        <AppBar center={hasLogo && logo} right={showSkipButton && skipButton} onBack={showBackButton && handleBack} />
      )}
      <div className={cn(styles.container, hasAppBar ? styles.hasAppBar : '')}>
        <div className="py-4 md:py-8">
          {plan && product ? (
            <CheckoutCreditPlan
              form={form}
              title="Complete your purchase"
              subtitle={subtitle}
              product={intervalPlan}
              onSuccess={handleSuccess}
            />
          ) : (
            <Text className="mx-auto text-center">Invalid plan or product</Text>
          )}
        </div>
      </div>
    </div>
  );
}
